'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  LayoutDashboard, KanbanSquare, Users, Settings, Sun, Moon,
  BarChart3, Inbox, MessageSquare, Sparkles, LogOut, User,
  CheckSquare, PanelLeft, Bot, Calendar,
  ChevronDown, Package, List as ListIcon, TrendingUp, Zap,
  FileText, Megaphone, ShieldCheck, Activity, Search,
  KeyRound, Database, ScrollText, Building2, Award,
  FileDown, CreditCard, Palette, ArrowLeft, ChevronRight, Menu, X, Server, Home, Clock,
  Film, FlaskConical, Target, Type, Image as ImageIconLucide,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useUIState } from '@/store/uiState';
import { prefetchRoute, RouteName } from '@/lib/prefetch';
import { useBoards } from '@/lib/query/hooks/useBoardsQuery';
import { useOrgFeatures } from '@/lib/query/hooks/useOrgFeaturesQuery';
import { NotificationPopover } from './notifications/NotificationPopover';
import { useResponsiveMode } from '@/hooks/useResponsiveMode';
import { MoreMenuSheet } from '@/components/navigation';
import { SkipLink } from '@/lib/a11y';
import { canUseIssenceSiteProperties } from '@/lib/orgCapabilities';

interface LayoutV2Props {
  children: React.ReactNode;
}

function getPageTitle(pathname: string): string {
  const map: Record<string, string> = {
    '/inbox': 'Inbox',
    '/messaging': 'Mensagens',
    '/boards': 'Boards',
    '/contacts': 'Contatos',
    '/activities': 'Tarefas',
    '/calendar': 'Calendário',
    '/dashboard': 'Visão Geral',
    '/reports': 'Relatórios',
    '/settings': 'Configurações',
    '/bots': 'Bots',
    '/templates': 'Modelos',
    '/admin': 'Painel Admin',
    '/admin/organizations': 'Organizações',
    '/admin/logs': 'Logs & Erros',
    '/admin/attendant-quality': 'Relatório Atendimento',
    '/admin/ai-usage': 'Uso de IA',
    '/admin/ai-quotas': 'Cotas de IA',
    '/admin/providers': 'Recursos IA',
    // sub-groups via ?group=

    '/admin/keys': 'Chaves Master',
    '/admin/agent-sandbox': 'AI Sandbox',
    '/admin/agent-sandbox/products': 'Produtos Sandbox',
    '/admin/ads': 'Relatório Meta Ads',
    '/admin/reports': 'Relatório Geral',
    '/admin/unanswered': 'Aguardando Resposta',
    '/admin/storage': 'Storage',
    '/admin/vercel': 'Vercel',
    '/admin/design-preview': 'Preview UI',
    '/admin/meta-ads-accounts': 'Orçamento Ads',
    '/admin/meta-ads/campaigns': 'Meta Ads · Campanha',
    '/admin/meta-ads/settings': 'Meta Ads · Configurações',
    '/admin/bot-users': 'Bot Telegram',
    '/admin/team': 'Equipe Admin',
    '/admin/property-images': 'Imagens de Imóveis',
  };
  for (const [key, label] of Object.entries(map)) {
    if (pathname === key || pathname.startsWith(key + '/')) return label;
  }
  return 'issence crm';
}

// ─── CRM Logo Icon ───────────────────────────────────────────────────────────
function CrmLogoIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true" className="flex-shrink-0">
      <rect x="1" y="7" width="18" height="12" rx="2.5" fill="#7c3aed" opacity="0.15" />
      <path d="M10 1L19 8H1L10 1Z" fill="#7c3aed" />
      <rect x="7" y="11" width="6" height="8" rx="1.5" fill="#7c3aed" />
    </svg>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type NavLeaf = {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  label: string;
  prefetch?: RouteName;
  badge?: number;
  soon?: boolean;
};
type NavSubBranch = {
  subKey: string;
  label: string;
  defaultOpen?: boolean;
  children: NavLeaf[];
};
type NavBranch = {
  key: string;
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  label: string;
  defaultOpen?: boolean;
  children: (NavLeaf | NavSubBranch)[];
};
type NavGroup = { group: string };
type NavEntry = NavLeaf | NavBranch | NavGroup;

const isGroup = (e: NavEntry): e is NavGroup => 'group' in e;
const isBranch = (e: NavEntry): e is NavBranch => 'children' in e;
const isSubBranch = (c: NavLeaf | NavSubBranch): c is NavSubBranch => 'subKey' in c;

// ─── NavItem V2 ──────────────────────────────────────────────────────────────

function NavItemV2({
  item,
  pathname,
  fullPath,
  clickedPath,
  onItemClick,
}: {
  item: NavLeaf;
  pathname: string;
  fullPath?: string;
  clickedPath?: string;
  onItemClick: (path: string) => void;
}) {
  const fp = fullPath ?? pathname;
  const isActive =
    clickedPath
      ? clickedPath === item.to
      : item.to.includes('?')
        ? fp === item.to || fp.startsWith(item.to + '&')
        : pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to + '/'));

  return (
    <Link
      href={item.to}
      onMouseEnter={() => item.prefetch && prefetchRoute(item.prefetch)}
      onClick={() => onItemClick(item.to)}
      className={`group flex items-center justify-between mx-2 px-4 h-9 rounded text-[13px] transition-colors ${
        isActive
          ? 'bg-primary-50/80 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
          : 'text-gray-600 dark:text-[#a0a0a0] font-medium hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-100'
      }`}
    >
      <span className="flex items-center gap-3 min-w-0">
        <item.icon
          className={`w-[14px] h-[14px] flex-shrink-0 ${
            isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-[#a0a0a0] group-hover:text-gray-600 dark:group-hover:text-[#a0a0a0]'
          }`}
          strokeWidth={1.5}
        />
        <span className="truncate">{item.label}</span>
      </span>
      {(item.badge ?? 0) > 0 && (
        <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
          {item.badge! > 99 ? '99+' : item.badge}
        </span>
      )}
    </Link>
  );
}

// ─── DropdownNavItem V2 ───────────────────────────────────────────────────────

function DropdownNavItemV2({
  item,
  pathname,
  fullPath,
  clickedPath,
  onItemClick,
}: {
  item: NavBranch;
  pathname: string;
  fullPath?: string;
  clickedPath?: string;
  onItemClick: (path: string) => void;
}) {
  const fp = fullPath ?? pathname;
  const isAnyActive = item.children.some(c => {
    if (isSubBranch(c)) return c.children.some(leaf =>
      leaf.to.includes('?') ? fp === leaf.to || fp.startsWith(leaf.to + '&') : pathname === leaf.to
    );
    return c.to.includes('?') ? fp === c.to || fp.startsWith(c.to + '&') : pathname === c.to || pathname.startsWith(c.to + '/');
  });
  const [open, setOpen] = useState(item.defaultOpen || isAnyActive);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="group flex items-center justify-between mx-2 px-4 h-9 w-[calc(100%-16px)] rounded text-[13px] font-medium transition-colors text-gray-600 dark:text-[#a0a0a0] hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-100"
      >
        <span className="flex items-center gap-3">
          <item.icon
            className="w-[14px] h-[14px] flex-shrink-0 text-gray-600 dark:text-[#a0a0a0]"
            strokeWidth={1.5}
          />
          {item.label}
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 flex-shrink-0 transition-all duration-200 text-gray-600 dark:text-[#a0a0a0] opacity-0 group-hover:opacity-100 ${open ? 'rotate-90 !opacity-100' : ''}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (() => {
        const hasSubs = item.children.some(isSubBranch);
        if (hasSubs) {
          // Mixed: sub-branches + leaves all at the same visual level
          return (
            <div className="mt-0.5 mb-1 ml-[31px] border-l border-gray-200 dark:border-[#333333]">
              {item.children.map((child) => {
                if (isSubBranch(child)) {
                  return (
                    <NavSubBranchItem
                      key={child.subKey}
                      item={child}
                      pathname={pathname}
                      fullPath={fp}
                      clickedPath={clickedPath}
                      onItemClick={onItemClick}
                    />
                  );
                }
                const active = clickedPath
                  ? clickedPath === child.to
                  : child.to.includes('?') ? fp === child.to || fp.startsWith(child.to + '&') : pathname === child.to;
                return (
                  <Link
                    key={child.to}
                    href={child.to}
                    onMouseEnter={() => child.prefetch && prefetchRoute(child.prefetch)}
                    onClick={() => onItemClick(child.to)}
                    className={`flex items-center pl-5 pr-2 h-9 w-full rounded text-[13px] transition-colors ${
                      active
                        ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/80 dark:bg-primary-900/20'
                        : 'font-normal text-gray-600 dark:text-[#a0a0a0] hover:text-gray-900 dark:hover:text-gray-100 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a]'
                    }`}
                  >
                    {child.label}
                  </Link>
                );
              })}
            </div>
          );
        }
        // Only leaves: indented container with border
        return (
          <div className="mt-0.5 mb-1 ml-[31px] border-l border-gray-200 dark:border-[#333333]">
            {(item.children as NavLeaf[]).map((child) => {
              if (child.soon) {
                return (
                  <span key={child.to} className="flex items-center h-9 pl-5 pr-4 rounded text-[13px] text-gray-400 dark:text-gray-600 cursor-default select-none">
                    {child.label}
                  </span>
                );
              }
              const active = clickedPath
                ? clickedPath === child.to
                : child.to.includes('?') ? fp === child.to || fp.startsWith(child.to + '&') : pathname === child.to || pathname.startsWith(child.to + '/');
              return (
                <Link
                  key={child.to}
                  href={child.to}
                  onMouseEnter={() => child.prefetch && prefetchRoute(child.prefetch)}
                  onClick={() => onItemClick(child.to)}
                  className={`flex items-center h-9 pl-5 pr-4 rounded text-[13px] transition-colors ${
                    active
                      ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/80 dark:bg-primary-900/20'
                      : 'font-normal text-gray-600 dark:text-[#a0a0a0] hover:text-gray-900 dark:hover:text-gray-100 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a]'
                  }`}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

// ─── NavSubBranchItem ─────────────────────────────────────────────────────────

function NavSubBranchItem({
  item, pathname, fullPath, clickedPath, onItemClick,
}: {
  item: NavSubBranch;
  pathname: string;
  fullPath?: string;
  clickedPath?: string;
  onItemClick: (path: string) => void;
}) {
  const fp = fullPath ?? pathname;
  const anyActive = item.children.some(c =>
    clickedPath ? clickedPath === c.to : c.to.includes('?') ? fp === c.to || fp.startsWith(c.to + '&') : pathname === c.to
  );
  const [open, setOpen] = useState(item.defaultOpen || anyActive);

  return (
    <div>
      {/* Botão com mesma largura do pai (mx-2 w-[calc(100%-16px)]) + indent via pl */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between pl-5 pr-4 h-9 w-[calc(100%-8px)] rounded text-[13px] font-medium transition-colors text-gray-600 dark:text-[#a0a0a0] hover:text-gray-900 dark:hover:text-gray-100 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a]"
      >
        <span>{item.label}</span>
        <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 text-gray-600 dark:text-[#a0a0a0] ${open ? 'rotate-90' : ''}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="ml-[20px] border-l border-gray-200 dark:border-[#333333]">
          {item.children.map(child => {
            if (child.soon) {
              return (
                <span key={child.to} className="flex items-center h-9 px-4 rounded text-[13px] text-gray-400 dark:text-gray-600 cursor-default select-none">
                  {child.label}
                </span>
              );
            }
            const active = clickedPath
              ? clickedPath === child.to
              : child.to.includes('?') ? fp === child.to || fp.startsWith(child.to + '&') : pathname === child.to;
            return (
              <Link
                key={child.to}
                href={child.to}
                onClick={() => onItemClick(child.to)}
                className={`flex items-center h-9 px-4 rounded text-[13px] transition-colors ${
                  active
                    ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/80 dark:bg-primary-900/20'
                    : 'font-normal text-gray-600 dark:text-[#a0a0a0] hover:text-gray-900 dark:hover:text-gray-100 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a]'
                }`}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Layout V2 ──────────────────────────────────────────────────────────

const LayoutV2: React.FC<LayoutV2Props> = ({ children }) => {
  const { darkMode, toggleDarkMode, setLayoutVersion } = useTheme();
  const { sidebarCollapsed, setSidebarCollapsed, mobileTitleOverride } = useUIState();
  const { user, loading, profile, signOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { mode, width: screenWidth } = useResponsiveMode();
  const searchParams = useSearchParams();
  const fullPath = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
  const isMobile = mode === 'mobile';
  const isTablet = mode === 'tablet';
  const isDesktop = mode === 'desktop';
  // iPad mini portrait ≈ 744px (mobile), iPad regular portrait ≈ 820px → show sidebar
  const showSidebar = screenWidth >= 820;
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [clickedPath, setClickedPath] = useState<string | undefined>(undefined);
  const [boardMobileDropdownOpen, setBoardMobileDropdownOpen] = useState(false);
  const [navSearchActive, setNavSearchActive] = useState(false);
  const [navSearchQuery, setNavSearchQuery] = useState('');
  const navSearchRef = useRef<HTMLInputElement>(null);

  const { data: orgFeatures } = useOrgFeatures();
  const messagingEnabled = orgFeatures?.messaging_enabled ?? true;
  // Meta Ads self-service: só admin da org, e só se a org foi habilitada no painel admin.
  const metaAdsSelfService = profile?.role === 'admin' && (orgFeatures?.meta_ads_self_service ?? false);
  const showIssenceSiteProperties = canUseIssenceSiteProperties(profile?.organization_id);
  const { data: boardsList = [] } = useBoards();

  const settingsSectionTitles: Record<string, string> = {
    imoveis: 'Imóveis IssenceSite',
    equipe: 'Equipe',
    tags: 'Tags',
    campos: 'Campos Personalizados',
    canais: 'WhatsApp',
    meta: 'Meta Ads',
    calendar: 'Google Calendar',
    api: 'API Keys',
    unidades: 'Unidades',
    deteccao: 'Detecção de Lead',
    motivo: 'Motivos de Perda',
  };
  const sParam = pathname === '/settings' ? (searchParams?.get('s') ?? null) : null;
  const pageTitle = (sParam && settingsSectionTitles[sParam]) ? settingsSectionTitles[sParam] : getPageTitle(pathname);

  useEffect(() => {
    if (loading) return;
    if (!user) router.replace('/login');
  }, [loading, user, router]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sidebarWidth = showSidebar ? (sidebarCollapsed ? '3.5rem' : '15rem') : '0px';
    document.documentElement.style.setProperty('--app-sidebar-width', sidebarWidth);
  }, [showSidebar, sidebarCollapsed]);

  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined')
        document.documentElement.style.setProperty('--app-sidebar-width', '0px');
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty('--app-bottom-nav-height', isMobile ? '56px' : '0px');
  }, [isMobile]);

  useEffect(() => { setIsMoreOpen(false); }, [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setNavSearchActive(true);
        setTimeout(() => navSearchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') { setNavSearchActive(false); setNavSearchQuery(''); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { setNavSearchActive(false); setNavSearchQuery(''); }, [pathname]);

  React.useEffect(() => {
    if (clickedPath) {
      const active = pathname === clickedPath || (clickedPath === '/boards' && pathname === '/pipeline');
      if (active) setClickedPath(undefined);
    }
  }, [pathname, clickedPath]);

  const userInitials = profile?.email?.substring(0, 2).toUpperCase() || 'U';
  const userName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.nickname || profile?.email || 'Usuário';
  const isAdminRoute = pathname?.startsWith('/admin') ?? false;

  // ─── Nav items ───────────────────────────────────────────────────────────

  const adminItems: NavEntry[] = profile?.is_platform_admin ? [
    { group: 'Performance' },
    { to: '/admin/reports', icon: FileDown, label: 'Relatório Geral' },
    { to: '/admin/unanswered', icon: Clock, label: 'Aguardando Resposta' },
    { to: '/admin/ads', icon: Megaphone, label: 'Relatório Meta Ads' },
    { to: '/admin/meta-ads-accounts', icon: CreditCard, label: 'Orçamento Ads' },
    { key: 'meta-ads-tool', icon: Megaphone, label: 'Meta Ads', defaultOpen: true, children: [
      { to: '/admin/meta-ads/campaigns', icon: Megaphone, label: 'Campanhas' },
      { to: '/admin/meta-ads/codes', icon: KeyRound, label: 'Códigos' },
      { to: '/admin/meta-ads/capi', icon: Zap, label: 'CAPI Eventos' },
      { subKey: 'meta-ads-settings', label: 'Configurações', defaultOpen: true, children: [
        { to: '/admin/meta-ads/settings?tab=contas', icon: Home, label: 'Contas' },
        { to: '/admin/meta-ads/settings?tab=identidades', icon: User, label: 'Identidades' },
        { to: '/admin/meta-ads/settings?tab=whatsapps', icon: MessageSquare, label: 'WhatsApps' },
        { to: '/admin/meta-ads/settings?tab=modelos', icon: FileText, label: 'Modelos de conversa' },
        { to: '/admin/meta-ads/settings?tab=publicos', icon: Target, label: 'Públicos' },
        { to: '/admin/meta-ads/settings?tab=criativos', icon: Film, label: 'Criativos' },
        { to: '/admin/meta-ads/settings?tab=textos', icon: Type, label: 'Textos e Títulos' },
      ] },
    ] },
    { to: '/admin/attendant-quality', icon: Award, label: 'Relatório Atendimento' },
    { to: '/admin/bot-users', icon: Bot, label: 'Bot Telegram' },

    { group: 'IA' },
    { key: 'ia-recursos', icon: Sparkles, label: 'Recursos', children: [
      { to: '/admin/providers?group=bots', icon: Bot, label: 'Bots' },
      { to: '/admin/providers?group=qualificacao', icon: CheckSquare, label: 'Qualificação' },
      { to: '/admin/providers?group=followup', icon: Zap, label: 'Follow-up' },
    ]},
    { key: 'agent-sandbox', icon: FlaskConical, label: 'AI Sandbox', children: [
      { to: '/admin/agent-sandbox', icon: FlaskConical, label: 'Testar agentes' },
      { to: '/admin/agent-sandbox/products', icon: Package, label: 'Produtos' },
    ]},
    { to: '/admin/ai-usage', icon: Activity, label: 'Uso de IA' },
    { to: '/admin/ai-quotas', icon: ShieldCheck, label: 'Cotas de IA' },
    { to: '/admin/keys', icon: KeyRound, label: 'Chaves Master' },

    { group: 'Plataforma' },
    { to: '/admin/organizations', icon: Building2, label: 'Organizações' },
    { to: '/admin/property-images', icon: ImageIconLucide, label: 'Imagens de Imóveis' },
    { to: '/admin/logs', icon: ScrollText, label: 'Logs & Erros' },
    { key: 'hospedagem', icon: Server, label: 'Uso de Hospedagem', children: [
      { to: '/admin/vercel', icon: TrendingUp, label: 'Vercel' },
      { to: '/admin/storage', icon: Database, label: 'Storage' },
      { to: '/admin/design-preview', icon: Palette, label: 'Preview UI' },
    ]},
  ] : [];

  const mainItems: NavEntry[] = [
    { to: '/admin/reports', icon: BarChart3, label: 'Relatório Geral' },
    ...(messagingEnabled ? [{ to: '/messaging', icon: MessageSquare, label: 'Mensagens' }] : []),
    ...(boardsList.length > 0
      ? [{ key: 'boards', icon: KanbanSquare, label: 'Boards', children: boardsList.map(b => ({ to: `/boards?board=${b.id}`, icon: b.isSystemContacts ? Users : KanbanSquare, label: b.name, prefetch: 'boards' as const })) }]
      : [{ to: '/boards', icon: KanbanSquare, label: 'Boards', prefetch: 'boards' as const }]),
    { key: 'listas', icon: ListIcon, label: 'Listas', children: [
      { to: '/contacts', icon: Users, label: 'Contatos', prefetch: 'contacts' as const },
      { to: '/settings/products', icon: Package, label: 'Produtos' },
      ...(showIssenceSiteProperties
        ? [{ to: '/settings?s=imoveis', icon: Home, label: 'Imóveis IssenceSite' } as NavLeaf]
        : []),
    ]},
    { key: 'calendario', icon: Calendar, label: 'Calendário', children: [
      { to: '/activities', icon: CheckSquare, label: 'Tarefas' },
      { to: '/calendar', icon: Calendar, label: 'Reuniões' },
    ]},
    { key: 'automacoes', icon: Zap, label: 'Automações', children: [
      { to: '/bots', icon: Bot, label: 'Bots' },
      { to: '/templates', icon: FileText, label: 'Modelos de Mensagens', soon: true },
    ]},
    { key: 'reports', icon: BarChart3, label: 'Relatórios', children: [
      { to: '/reports?tab=leads', icon: Users, label: 'Leads', prefetch: 'reports' as const },
      { to: '/admin/attendant-quality', icon: Award, label: 'Atendimento' },
      { to: '/reports?tab=vendas', icon: CreditCard, label: 'Vendas', prefetch: 'reports' as const },
    ]},
    ...(metaAdsSelfService ? [{ key: 'meta-ads', icon: Megaphone, label: 'Meta Ads', children: [
      { to: '/meta-ads/campaigns', icon: Megaphone, label: 'Campanhas' },
      { to: '/meta-ads/codes', icon: KeyRound, label: 'Códigos' },
      { to: '/meta-ads/capi', icon: Zap, label: 'CAPI Eventos' },
      { subKey: 'meta-ads-settings', label: 'Configurações', children: [
        { to: '/meta-ads/settings?tab=contas', icon: Home, label: 'Contas' },
        { to: '/meta-ads/settings?tab=identidades', icon: User, label: 'Identidades' },
        { to: '/meta-ads/settings?tab=whatsapps', icon: MessageSquare, label: 'WhatsApps' },
        { to: '/meta-ads/settings?tab=modelos', icon: FileText, label: 'Modelos de conversa' },
        { to: '/meta-ads/settings?tab=publicos', icon: Target, label: 'Públicos' },
        { to: '/meta-ads/settings?tab=criativos', icon: Film, label: 'Criativos' },
        { to: '/meta-ads/settings?tab=textos', icon: Type, label: 'Textos e Títulos' },
      ] },
    ]}] as NavEntry[] : []),
    { key: 'configuracoes', icon: Settings, label: 'Configurações', children: [
      { subKey: 'preferencias', label: 'Preferências', children: [
        { to: '/settings?s=board', icon: Settings, label: 'Board' },
        { to: '/settings?s=tags', icon: Settings, label: 'Tags' },
        { to: '/settings?s=campos', icon: Settings, label: 'Campos Personalizados' },
        { to: '/settings?s=motivo', icon: Settings, label: 'Motivos de Perda' },
        { to: '/settings?s=unidades', icon: Settings, label: 'Unidades' },
      ]},
      { subKey: 'integracoes', label: 'Integrações', children: [
        { to: '/settings?s=canais', icon: Settings, label: 'WhatsApp' },
        { to: '/settings?s=meta', icon: Settings, label: 'Meta Ads' },
        { to: '/settings?s=deteccao', icon: Settings, label: 'Detecção de lead' },
        { to: '/settings?s=calendar', icon: Settings, label: 'Calendário' },
        { to: '/settings?s=api', icon: Settings, label: 'API Keys' },
      ]},
    ]},
    ...(profile?.is_platform_admin ? [{ to: '/admin', icon: ShieldCheck, label: 'Admin (Issence)' } as NavLeaf] : []),
  ];

  const items = isAdminRoute && adminItems.length > 0 ? adminItems : mainItems;

  // Fecha drawer ao navegar
  useEffect(() => { setIsMobileMenuOpen(false); }, [pathname]);

  if (!loading && !user) return null;

  // Filtra itens planos quando há busca
  const q = navSearchQuery.toLowerCase().trim();
  const filteredLeaves: NavLeaf[] = q
    ? items.flatMap((item) => {
        if (isGroup(item)) return [];
        if (isBranch(item)) return item.children.flatMap((c): NavLeaf[] => {
          if (isSubBranch(c)) return c.children.filter(leaf => leaf.label.toLowerCase().includes(q));
          return c.label.toLowerCase().includes(q) ? [c] : [];
        });
        return (item as NavLeaf).label.toLowerCase().includes(q) ? [item as NavLeaf] : [];
      })
    : [];

  // Buscador — plano, h-14 + border-b no MESMO elemento (idêntico à barra do funil, pra alinhar)
  const NavSearchBar = () =>
    navSearchActive ? (
      <div className="flex items-center gap-2 h-14 px-4 text-sm border-b border-slate-200 dark:border-white/10">
        <Search className="w-[13px] h-[13px] flex-shrink-0 text-gray-400" strokeWidth={1.5} />
        <input
          ref={navSearchRef}
          value={navSearchQuery}
          onChange={(e) => setNavSearchQuery(e.target.value)}
          onBlur={() => { if (!navSearchQuery) { setNavSearchActive(false); } }}
          placeholder="Pesquisa rápida..."
          className="flex-1 bg-transparent outline-none text-gray-700 dark:text-gray-200 placeholder-gray-600 text-sm"
        />
        {navSearchQuery && (
          <button onClick={() => { setNavSearchQuery(''); setNavSearchActive(false); }}>
            <X size={12} className="text-gray-400" />
          </button>
        )}
      </div>
    ) : (
      <button
        onClick={() => { setNavSearchActive(true); setTimeout(() => navSearchRef.current?.focus(), 50); }}
        className="w-full flex items-center gap-2 h-14 px-4 text-gray-600 dark:text-[#a0a0a0] text-sm hover:text-gray-900 dark:hover:text-gray-100 transition-colors border-b border-slate-200 dark:border-white/10"
      >
        <Search className="w-[13px] h-[13px] flex-shrink-0" strokeWidth={1.5} />
        <span className="flex-1 text-left">Pesquisa rápida...</span>
      </button>
    );

  // Nav renderizado tanto no sidebar desktop quanto no drawer mobile
  const renderNavItems = (onNavigate?: () => void) => items.map((item, idx) => {
    if (isGroup(item)) {
      return (
        <div key={`g-${item.group}`}>
          {idx > 0 && <div className="border-t border-gray-100 dark:border-[#2a2a2a] mx-2 mt-2 mb-1" />}
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-600 px-[22px] pt-3 pb-1 leading-none">
            {item.group}
          </p>
        </div>
      );
    }
    if (isBranch(item)) {
      return (
        <DropdownNavItemV2
          key={item.key}
          item={item}
          pathname={pathname}
          fullPath={fullPath}
          clickedPath={clickedPath}
          onItemClick={(p) => { setClickedPath(p); onNavigate?.(); }}
        />
      );
    }
    return (
      <NavItemV2
        key={(item as NavLeaf).to}
        item={item as NavLeaf}
        pathname={pathname}
        clickedPath={clickedPath}
        onItemClick={(p) => { setClickedPath(p); onNavigate?.(); }}
      />
    );
  });

  return (
    <div className="flex h-screen overflow-hidden bg-[#fafafa] dark:bg-[#111111]">
      <SkipLink targetId="main-content" />

      {isMobile && <MoreMenuSheet isOpen={isMoreOpen} onClose={() => setIsMoreOpen(false)} />}

      {/* Drawer — only when sidebar is hidden (< 820px) */}
      {!showSidebar && isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-[45] bg-black/60"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <aside className="fixed left-0 top-0 bottom-0 z-[55] w-64 bg-white dark:bg-[#242424] border-r border-slate-200 dark:border-white/10 flex flex-col shadow-xl">
            <div className="h-14 flex items-center justify-between px-5 border-b border-slate-200 dark:border-white/10 flex-shrink-0">
              <span className="text-[15px] font-bold text-gray-900 dark:text-gray-100 tracking-tight">issence crm</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
              >
                <X size={15} strokeWidth={1.5} />
              </button>
            </div>
            <NavSearchBar />
            <nav className="flex-1 overflow-y-auto py-2">
              {q ? (
                filteredLeaves.length === 0
                  ? <p className="px-5 py-4 text-sm text-gray-400">Nenhum resultado</p>
                  : filteredLeaves.map((leaf) => (
                      <NavItemV2 key={leaf.to} item={leaf} pathname={pathname} fullPath={fullPath} clickedPath={clickedPath}
                        onItemClick={(p) => { setClickedPath(p); setIsMobileMenuOpen(false); }} />
                    ))
              ) : renderNavItems(() => setIsMobileMenuOpen(false))}
            </nav>

            {/* Rodapé conta — mobile drawer */}
            <div className="border-t border-slate-200 dark:border-white/10 shrink-0">
              <div className="px-4 py-2.5 border-b border-gray-100 dark:border-[#2a2a2a]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 dark:text-gray-600">Conta</p>
              </div>
              <div className="py-1">
                <Link
                  href="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                >
                  <User size={15} strokeWidth={1.5} />
                  <span>{userName}</span>
                </Link>
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                >
                  {darkMode ? <Sun size={15} strokeWidth={1.5} /> : <Moon size={15} strokeWidth={1.5} />}
                  {darkMode ? 'Tema claro' : 'Tema escuro'}
                </button>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); signOut(); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                >
                  <LogOut size={15} strokeWidth={1.5} />
                  Sair
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      {/* Sidebar V2 — iPad regular (≥820px) and desktop */}
      {showSidebar && (
        <aside
          className={`flex print:hidden flex-col z-20 bg-white dark:bg-[#242424] border-r border-slate-200 dark:border-white/10 transition-all duration-300 ease-in-out flex-shrink-0 ${
            sidebarCollapsed ? 'w-[56px]' : 'w-60'
          }`}
          aria-label="Menu principal"
        >
          {/* Logo + email */}
          <div className={`h-14 flex items-center border-b border-slate-200 dark:border-white/10 flex-shrink-0 ${
            sidebarCollapsed ? 'justify-center px-0' : 'px-4 justify-between gap-2'
          }`}>
            {sidebarCollapsed ? (
              <button
                onClick={() => setSidebarCollapsed(false)}
                title="Expandir sidebar"
                className="flex items-center justify-center transition-opacity hover:opacity-80"
              >
                <CrmLogoIcon size={22} />
              </button>
            ) : (
              <>
                <div className="flex items-center gap-2.5 min-w-0">
                  <CrmLogoIcon size={20} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">issence crm</p>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  title="Recolher sidebar"
                  className="shrink-0 flex items-center justify-center w-7 h-7 rounded text-gray-600 dark:text-[#a0a0a0] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  <PanelLeft size={16} strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>

          {/* Buscador — só quando expandido */}
          {!sidebarCollapsed && <NavSearchBar />}
          {sidebarCollapsed && (
            <div className="flex justify-center py-2">
              <button
                onClick={() => { setNavSearchActive(true); setTimeout(() => navSearchRef.current?.focus(), 50); }}
                className="w-10 h-9 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a] transition-colors"
                title="Pesquisa rápida (⌘K)"
              >
                <Search className="w-[15px] h-[15px]" strokeWidth={1.5} />
              </button>
            </div>
          )}

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-2" aria-label="Navegação">
            {!sidebarCollapsed && q ? (
              filteredLeaves.length === 0
                ? <p className="px-5 py-4 text-sm text-gray-400">Nenhum resultado</p>
                : filteredLeaves.map((leaf) => (
                    <NavItemV2 key={leaf.to} item={leaf} pathname={pathname} fullPath={fullPath} clickedPath={clickedPath} onItemClick={setClickedPath} />
                  ))
            ) : items.map((item, idx) => {
              if (isGroup(item)) {
                if (sidebarCollapsed) return null;
                return (
                  <div key={`g-${item.group}`}>
                    {idx > 0 && <div className="border-t border-gray-100 dark:border-[#2a2a2a] mx-2 mt-2 mb-1" />}
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-500 px-[22px] pt-3 pb-1 leading-none">
                      {item.group}
                    </p>
                  </div>
                );
              }
              if (sidebarCollapsed) {
                const firstChild = isBranch(item) ? item.children[0] : item as NavLeaf;
                const leaf: NavLeaf | undefined = firstChild && isSubBranch(firstChild as NavLeaf | NavSubBranch) ? (firstChild as NavSubBranch).children[0] : firstChild as NavLeaf | undefined;
                if (!leaf) return null;
                const active = pathname === leaf.to || pathname.startsWith(leaf.to + '/');
                return (
                  <Link
                    key={leaf.to}
                    href={leaf.to}
                    title={isBranch(item) ? item.label : leaf.label}
                    className={`flex items-center justify-center mx-auto h-9 w-10 rounded transition-colors ${
                      active
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'text-gray-400 dark:text-gray-500 hover:bg-[#f3f4f6] dark:hover:bg-[#2a2a2a] hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {isBranch(item)
                      ? <item.icon className="w-[15px] h-[15px]" strokeWidth={1.5} />
                      : React.createElement((item as NavLeaf).icon, { className: 'w-[15px] h-[15px]', strokeWidth: 1.5 })}
                  </Link>
                );
              }
              if (isBranch(item)) {
                return (
                  <DropdownNavItemV2
                    key={item.key}
                    item={item}
                    pathname={pathname}
                    fullPath={fullPath}
                    clickedPath={clickedPath}
                    onItemClick={setClickedPath}
                  />
                );
              }
              return (
                <NavItemV2
                  key={(item as NavLeaf).to}
                  item={item as NavLeaf}
                  pathname={pathname}
                  clickedPath={clickedPath}
                  onItemClick={setClickedPath}
                />
              );
            })}
            </nav>

          {/* Perfil — rodapé (estilo Kommo): avatar + dropdown pra cima com Tema/Sair, e sino ao lado */}
          <div className="relative flex items-center border-t border-slate-200 dark:border-white/10 px-2 h-14 shrink-0">
            {isUserMenuOpen && (
              <div className="absolute bottom-full mb-1 left-2 right-2 z-50 min-w-[200px] bg-white dark:bg-[#1e1e1e] rounded shadow-lg border border-slate-200 dark:border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-[#2a2a2a]">
                  <p className="text-[11px] text-gray-600 dark:text-[#a0a0a0] truncate">{profile?.email}</p>
                </div>
                <div className="py-1">
                  <Link href="/profile" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#242424] transition-colors">
                    <User size={14} strokeWidth={1.5} /> Meu Perfil
                  </Link>
                  <Link href="/settings?s=equipe" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#242424] transition-colors">
                    <Users size={14} strokeWidth={1.5} /> Equipe
                  </Link>
                  {profile?.is_platform_admin && (
                    <Link href="/admin/team" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#242424] transition-colors">
                      <Users size={14} strokeWidth={1.5} /> Equipe Admin
                    </Link>
                  )}
                  <button onClick={toggleDarkMode} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#242424] transition-colors">
                    {darkMode ? <Sun size={14} strokeWidth={1.5} /> : <Moon size={14} strokeWidth={1.5} />}
                    {darkMode ? 'Tema claro' : 'Tema escuro'}
                  </button>
                </div>
                <div className="border-t border-gray-100 dark:border-[#2a2a2a] py-1">
                  <button onClick={() => { setIsUserMenuOpen(false); signOut(); }} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#242424] transition-colors">
                    <LogOut size={14} strokeWidth={1.5} /> Sair
                  </button>
                </div>
              </div>
            )}

            {sidebarCollapsed ? (
              <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center justify-center w-full hover:opacity-80 transition-opacity" title="Perfil">
                {profile?.avatar_url ? (
                  <Image src={profile.avatar_url} alt="Avatar" width={28} height={28} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gray-300 dark:bg-[#444] flex items-center justify-center text-[11px] font-semibold text-gray-700 dark:text-gray-200">{userInitials}</div>
                )}
              </button>
            ) : (
              <div className="flex w-full items-center gap-1">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex flex-1 min-w-0 items-center gap-2.5 rounded-lg p-1 text-left hover:bg-gray-100 dark:hover:bg-[#242424] transition-colors">
                  {profile?.avatar_url ? (
                    <Image src={profile.avatar_url} alt="Avatar" width={28} height={28} className="w-7 h-7 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-300 dark:bg-[#444] flex items-center justify-center text-[11px] font-semibold text-gray-700 dark:text-gray-200 shrink-0">{userInitials}</div>
                  )}
                  <div className="min-w-0 text-left">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100 leading-tight">{userName}</p>
                    <p className="truncate text-[11px] text-gray-600 dark:text-[#a0a0a0] leading-tight">{profile?.email}</p>
                  </div>
                </button>
                <NotificationPopover compact openUp />
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Topbar V2 — escondido no Boards/Contatos/Mensagens (desktop): a página já tem sua própria barra */}
        {!(showSidebar && (pathname.startsWith('/boards') || pathname.startsWith('/contacts') || pathname.startsWith('/messaging') || pathname.startsWith('/bots'))) && (
        <header
          className="h-14 bg-white dark:bg-[#242424] border-b border-slate-200 dark:border-white/10 flex items-center justify-between px-5 z-40 shrink-0 print:hidden"
          role="banner"
        >
          {/* Esquerda */}
          <div className="flex items-center gap-2">
            {!showSidebar && (
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="w-8 h-9 flex items-center justify-center rounded text-gray-500 dark:text-[#888] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
              >
                <Menu size={17} strokeWidth={1.5} />
              </button>
            )}
            {showSidebar && (
              <h1 className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                {pageTitle}
              </h1>
            )}
          </div>

          {/* Centro — título da página no mobile */}
          {!showSidebar && (
            pathname.startsWith('/boards') && mobileTitleOverride ? (
              <div className="absolute left-1/2 -translate-x-1/2">
                <button
                  type="button"
                  onClick={() => setBoardMobileDropdownOpen(v => !v)}
                  className="flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100"
                >
                  <span className="max-w-[160px] truncate">{mobileTitleOverride}</span>
                  <ChevronDown size={13} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                </button>
                {boardMobileDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-[49]" onClick={() => setBoardMobileDropdownOpen(false)} />
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 z-50 bg-white dark:bg-[#2a2a2a] rounded-xl shadow-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                      {boardsList.map(board => (
                        <button key={board.id} type="button"
                          onClick={() => { router.push(`/boards?board=${board.id}`); setBoardMobileDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${mobileTitleOverride === board.name ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-300' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                        >
                          <span className="w-2 h-2 rounded-full shrink-0 bg-slate-300 dark:bg-slate-600" />
                          <span className="truncate">{board.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : mobileTitleOverride ? (
              <h1 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-900 dark:text-gray-100 pointer-events-none max-w-[55%] truncate text-center">
                {mobileTitleOverride}
              </h1>
            ) : (
              <h1 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-900 dark:text-gray-100 pointer-events-none">
                {pageTitle}
              </h1>
            )
          )}

          {/* Direita */}
          <div className="flex items-center gap-0.5" />
        </header>
        )}

        {/* Content */}
        <main
          id="main-content"
          className={`flex-1 bg-[#f3f4f6] dark:bg-white/5 ${
            pathname === '/messaging' || pathname.startsWith('/messaging/') ||
            pathname === '/boards' || pathname.startsWith('/boards/') ||
            pathname === '/contacts' || pathname.startsWith('/contacts/') ||
            pathname.startsWith('/bots')
              ? 'overflow-hidden p-0'
              : pathname === '/admin/reports'
                ? 'overflow-auto p-0'
                : 'overflow-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-[calc(1rem+var(--app-bottom-nav-height,0px)+var(--app-safe-area-bottom,0px))]'
          }`}
          tabIndex={-1}
        >
          {children}
        </main>

      </div>
    </div>
  );
};

export default LayoutV2;
